import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useCategorySelection } from "hooks/categorySelection.hook"
import { useGetModelNumberImageLink } from "hooks/api/modelNumbers/modelNumbers.hook"
import {
	MIXED_LABEL,
	batchImagePreviewMessage,
	collectDimensionGroups,
	deriveBatchFields,
	isMixed,
	resolveBatchSections,
	resolveBatchSelectionKind,
} from "helpers/batchFurniture.helper"
import {
	CUSTOM_TOP_LEVEL_CATEGORY,
	OTHER_TOP_LEVEL_CATEGORY,
	SYNTHETIC_TOP_LEVEL_OPTIONS,
} from "../FurnitureSidebar/furniture.constants"
import type { TModelNumberTarget } from "../FurnitureSidebar/layout/ModelNumberSection/SelectModelNumberModal/SelectModelNumberModal"
import type { TFurniture, TFurnitureStatus } from "types/elevationReferences.type"

/**
 * Form state for the batch furniture panel (REQ-403).
 *
 * The single-object form seeds itself from one furniture; this one seeds itself from what the whole
 * selection AGREES on. A field the selection disagrees on starts EMPTY with a `Mixed` placeholder
 * rather than pre-filled with someone's value — that emptiness is load-bearing: an untouched field
 * is omitted from the request, which is exactly how the objects keep their differing values.
 *
 * Everything derived (the shared values, which sections exist, the preview copy, the dimension
 * groups) comes from `helpers/batchFurniture.helper`, so it stays unit-testable on its own.
 */
export const useBatchFurnitureForm = (furniture: TFurniture[]) => {
	const fields = useMemo(() => deriveBatchFields(furniture), [furniture])
	const kind = useMemo(() => resolveBatchSelectionKind(furniture), [furniture])
	const sections = useMemo(() => resolveBatchSections(kind), [kind])
	const dimensionGroups = useMemo(() => collectDimensionGroups(furniture), [furniture])

	// "Other" and "Custom" selections have no category at any level by definition, so the top-level
	// select is driven by the matching synthetic option rather than by a backend value.
	const sharedTopLevel = isMixed(fields.topLevelCategory) ? null : fields.topLevelCategory
	const defaultTopLevelCategory =
		kind === "other" ? OTHER_TOP_LEVEL_CATEGORY : kind === "custom" ? CUSTOM_TOP_LEVEL_CATEGORY : sharedTopLevel
	const defaultSuperCategory = isMixed(fields.superCategory) ? null : fields.superCategory
	const defaultModelCategoryId = isMixed(fields.objectCategoryId) ? null : fields.objectCategoryId
	const defaultModelCategoryName = isMixed(fields.objectCategoryName) ? null : fields.objectCategoryName

	const categorySelection = useCategorySelection({
		defaultTopLevelCategory,
		defaultSuperCategory,
		defaultModelCategoryId,
		defaultModelCategoryName,
		extraTopLevelOptions: SYNTHETIC_TOP_LEVEL_OPTIONS,
	})

	/**
	 * Resync the three selects whenever the BACKEND moves the selection's category — after a batch
	 * category change, or after a cross-category model number is applied. Same mechanism (and same
	 * reason) as the single-object form's: `syncDefaults`'s identity only changes when one of the
	 * backend values does, so this fires on exactly those transitions and not on the object-identity
	 * churn of a cache splice.
	 */
	const { syncDefaults: syncCategoryDefaults } = categorySelection
	useEffect(() => {
		syncCategoryDefaults()
	}, [syncCategoryDefaults])

	const sharedWidth = isMixed(fields.width) ? null : fields.width
	const sharedHeight = isMixed(fields.height) ? null : fields.height
	const sharedDepth = isMixed(fields.depth) ? null : fields.depth
	const sharedShelves = isMixed(fields.adjustedShelves) ? null : fields.adjustedShelves
	const sharedNotes = isMixed(fields.notes) ? "" : fields.notes
	const sharedName = isMixed(fields.name) ? "" : fields.name
	const sharedPrice = isMixed(fields.price) ? null : fields.price
	// REQ-431. Gated PER FIELD, like the category levels: note and price each show their own `Mixed`
	// placeholder independently, so a selection that agrees on one but disagrees on the other only
	// flags the one that actually disagrees (review feedback: showing BOTH as Mixed when only one
	// differs hides real, shared information the panel could otherwise display). Whether the section
	// is expanded at all is a separate question — `hasCustomization` — since a selection split
	// between "has one" and "has none" must expand even in the rare case its note/price happen to
	// coincide; only a selection that unanimously has none stays collapsed.
	const isCustomizationNoteMixed = isMixed(fields.customizationNote)
	const isCustomizationPriceAdjustmentMixed = isMixed(fields.customizationPriceAdjustment)
	const shouldExpandCustomization = isMixed(fields.hasCustomization) ? true : fields.hasCustomization
	// Every OTHER shared-* value here is a primitive, so the resync effects below only refire when
	// the derived VALUE actually changes — these already are (per-field, unlike the single combined
	// object this used to be), which also means `spliceFurnitureIntoCache` giving an edited item a
	// brand-new object identity on every commit (including ones that never touch `customization`)
	// can no longer wipe an in-progress note: the effect's dependency array compares these primitives
	// by VALUE like every sibling field, and an unrelated commit doesn't change them.
	const sharedCustomizationNote = isMixed(fields.customizationNote) ? "" : fields.customizationNote
	const sharedCustomizationPriceAdjustment = isMixed(fields.customizationPriceAdjustment)
		? null
		: fields.customizationPriceAdjustment

	const [width, setWidth] = useState<number | null>(sharedWidth)
	const [height, setHeight] = useState<number | null>(sharedHeight)
	const [depth, setDepth] = useState<number | null>(sharedDepth)
	const [adjustableShelves, setAdjustableShelves] = useState<number | null>(sharedShelves)
	const [notes, setNotes] = useState<string>(sharedNotes)
	const [name, setName] = useState<string>(sharedName)
	const [price, setPrice] = useState<number | null>(sharedPrice)
	const [isCustomizationExpanded, setIsCustomizationExpanded] = useState(shouldExpandCustomization)
	const [customizationNote, setCustomizationNote] = useState(sharedCustomizationNote)
	const [customizationPriceAdjustment, setCustomizationPriceAdjustment] = useState<number | null>(
		sharedCustomizationPriceAdjustment,
	)

	// The four segmented attachment rows are committed on click rather than on blur, so they also
	// keep local state: without it the segment the user just pressed would stay unlit until the
	// response landed. `null` is "the selection disagrees" → no segment lit.
	const sharedHasLocks = isMixed(fields.hasLocks) ? null : fields.hasLocks ? "yes" : "no"
	const sharedHasFinishedBacks = isMixed(fields.hasFinishedBacks) ? null : fields.hasFinishedBacks ? "yes" : "no"
	const sharedFillings = isMixed(fields.fillings) ? null : fields.fillings
	const sharedFinishingEnds = isMixed(fields.finishingEnds) ? null : fields.finishingEnds
	// REQ-449 — same "local state committed on blur" reasoning as the shelves field: `NumberInputRow`
	// is free-entry, so it needs local state to stay editable between keystrokes.
	const sharedCountertopGrommets = isMixed(fields.countertopGrommets) ? null : fields.countertopGrommets
	const sharedCountertopWallCleats = isMixed(fields.countertopWallCleats) ? null : fields.countertopWallCleats
	const [hasLocks, setHasLocks] = useState<"yes" | "no" | null>(sharedHasLocks)
	const [hasFinishedBacks, setHasFinishedBacks] = useState<"yes" | "no" | null>(sharedHasFinishedBacks)
	const [fillings, setFillings] = useState<number | null>(sharedFillings)
	const [finishingEnds, setFinishingEnds] = useState<number | null>(sharedFinishingEnds)
	const [countertopGrommets, setCountertopGrommets] = useState<number | null>(sharedCountertopGrommets)
	const [countertopWallCleats, setCountertopWallCleats] = useState<number | null>(sharedCountertopWallCleats)

	useEffect(() => setHasLocks(sharedHasLocks), [sharedHasLocks])
	useEffect(() => setHasFinishedBacks(sharedHasFinishedBacks), [sharedHasFinishedBacks])
	useEffect(() => setFillings(sharedFillings), [sharedFillings])
	useEffect(() => setFinishingEnds(sharedFinishingEnds), [sharedFinishingEnds])
	useEffect(() => setCountertopGrommets(sharedCountertopGrommets), [sharedCountertopGrommets])
	useEffect(() => setCountertopWallCleats(sharedCountertopWallCleats), [sharedCountertopWallCleats])

	useEffect(() => setWidth(sharedWidth), [sharedWidth])
	useEffect(() => setHeight(sharedHeight), [sharedHeight])
	useEffect(() => setDepth(sharedDepth), [sharedDepth])
	useEffect(() => setAdjustableShelves(sharedShelves), [sharedShelves])
	useEffect(() => setNotes(sharedNotes), [sharedNotes])
	useEffect(() => setName(sharedName), [sharedName])
	useEffect(() => setPrice(sharedPrice), [sharedPrice])
	useEffect(() => {
		setIsCustomizationExpanded(shouldExpandCustomization)
		setCustomizationNote(sharedCustomizationNote)
		setCustomizationPriceAdjustment(sharedCustomizationPriceAdjustment)
	}, [shouldExpandCustomization, sharedCustomizationNote, sharedCustomizationPriceAdjustment])

	/**
	 * Puts every local field back on what the objects actually hold.
	 *
	 * The panel needs this because a REJECTED edit changes nothing: the derived shared values come out
	 * identical, so none of the resync effects above can fire, and a field the user typed into (or an
	 * optimistically-lit segment, or a category select a pick already moved) would keep showing a
	 * value the backend never accepted. The same applies to a clear the endpoint has no null for.
	 *
	 * Reads through a ref rather than the closure so the identity can stay stable and the values are
	 * always the latest render's, not the one that created the handler.
	 */
	const sharedRef = useRef<{
		width: number | null
		height: number | null
		depth: number | null
		shelves: number | null
		notes: string
		name: string
		price: number | null
		hasLocks: "yes" | "no" | null
		hasFinishedBacks: "yes" | "no" | null
		fillings: number | null
		finishingEnds: number | null
		countertopGrommets: number | null
		countertopWallCleats: number | null
		customizationExpanded: boolean
		customizationNote: string
		customizationPriceAdjustment: number | null
	}>({
		width: sharedWidth,
		height: sharedHeight,
		depth: sharedDepth,
		shelves: sharedShelves,
		notes: sharedNotes,
		name: sharedName,
		price: sharedPrice,
		hasLocks: sharedHasLocks,
		hasFinishedBacks: sharedHasFinishedBacks,
		fillings: sharedFillings,
		finishingEnds: sharedFinishingEnds,
		countertopGrommets: sharedCountertopGrommets,
		countertopWallCleats: sharedCountertopWallCleats,
		customizationExpanded: shouldExpandCustomization,
		customizationNote: sharedCustomizationNote,
		customizationPriceAdjustment: sharedCustomizationPriceAdjustment,
	})
	sharedRef.current = {
		width: sharedWidth,
		height: sharedHeight,
		depth: sharedDepth,
		shelves: sharedShelves,
		notes: sharedNotes,
		name: sharedName,
		price: sharedPrice,
		hasLocks: sharedHasLocks,
		hasFinishedBacks: sharedHasFinishedBacks,
		fillings: sharedFillings,
		finishingEnds: sharedFinishingEnds,
		countertopGrommets: sharedCountertopGrommets,
		countertopWallCleats: sharedCountertopWallCleats,
		customizationExpanded: shouldExpandCustomization,
		customizationNote: sharedCustomizationNote,
		customizationPriceAdjustment: sharedCustomizationPriceAdjustment,
	}

	const resyncFromShared = useCallback(() => {
		const current = sharedRef.current
		setWidth(current.width)
		setHeight(current.height)
		setDepth(current.depth)
		setAdjustableShelves(current.shelves)
		setNotes(current.notes)
		setName(current.name)
		setPrice(current.price)
		setHasLocks(current.hasLocks)
		setHasFinishedBacks(current.hasFinishedBacks)
		setFillings(current.fillings)
		setFinishingEnds(current.finishingEnds)
		setCountertopGrommets(current.countertopGrommets)
		setCountertopWallCleats(current.countertopWallCleats)
		setIsCustomizationExpanded(current.customizationExpanded)
		setCustomizationNote(current.customizationNote)
		setCustomizationPriceAdjustment(current.customizationPriceAdjustment)
		syncCategoryDefaults()
	}, [syncCategoryDefaults])

	/** `Mixed` for a field the selection disagrees on, `undefined` for one it agrees on. */
	const mixedPlaceholder = (mixed: boolean) => (mixed ? MIXED_LABEL : undefined)

	// Shown only when ALL THREE dimensions are shared: a linear-feet figure derived from one axis of
	// a partly-Mixed selection would name a quantity none of the selected objects actually has.
	const showLnFt = !isMixed(fields.width) && !isMixed(fields.height) && !isMixed(fields.depth)

	const previewMessage = batchImagePreviewMessage(kind, isMixed(fields.modelNumber))
	const sharedModelNumber = isMixed(fields.modelNumber) ? null : fields.modelNumber
	// No local state, same reasoning as the eref select below: it commits immediately on pick, so
	// there's nothing to keep in sync between renders — the Select reads this derived value directly.
	const sharedDeliveryDate = isMixed(fields.deliveryDate) ? null : fields.deliveryDate
	// #1346 — the same rule for the custom link: a selection that disagrees hands over `null`, which
	// the chip renders as `Mixed` and the picker treats as "nothing selected yet".
	const sharedCustomModelNumber = isMixed(fields.customModelNumber) ? null : fields.customModelNumber
	// The catalogue image lives behind a signed download link, exactly as in the single-object
	// sidebar — `image_from_stevens_catalogue` on the furniture payload is not a usable URL. Only
	// requested when the whole selection agrees on one model number; there is no image otherwise.
	const { data: catalogueImage } = useGetModelNumberImageLink(sharedModelNumber?.id ?? 0)
	const imageUrl = catalogueImage?.download_url ?? null

	/**
	 * What the model-number picker is picking FOR. A field the selection disagrees on is handed over
	 * as "unset", which is exactly how the picker already treats a single object that has none: no
	 * category → it opens on the catalogue-wide scope, and every pick there routes through the
	 * arbitrary-model-number endpoint, which is the only one that accepts a mixed-category batch.
	 *
	 * `detected_model_numbers` is the UNION across the selection, so an ML suggestion keeps its
	 * `ML choice` tag even when only some of the objects were detected as that model.
	 */
	const modelNumberTarget = useMemo<TModelNumberTarget>(() => {
		const first = furniture[0]
		const detected = new Set<string>()
		for (const item of furniture) {
			for (const detectedModel of item.detected_model_numbers ?? []) detected.add(detectedModel)
		}
		return {
			category: isMixed(fields.objectCategoryId) ? null : (first?.category ?? null),
			super_category: isMixed(fields.superCategory) ? null : (first?.super_category ?? null),
			top_level_category: isMixed(fields.topLevelCategory) ? null : (first?.top_level_category ?? null),
			detected_model_numbers: [...detected],
			selected_model_number: sharedModelNumber,
			width: isMixed(fields.width) ? null : fields.width,
			height: isMixed(fields.height) ? null : fields.height,
			depth: isMixed(fields.depth) ? null : fields.depth,
		}
	}, [
		furniture,
		fields.objectCategoryId,
		fields.superCategory,
		fields.topLevelCategory,
		fields.width,
		fields.height,
		fields.depth,
		sharedModelNumber,
	])

	const status: TFurnitureStatus | null = isMixed(fields.status) ? null : (fields.status ?? "work_in_progress")

	// The `+` half of REQ-431's header button. Local-only: the caller commits the empty
	// customization to the server in the same tick (see `CustomizationSection`'s doc comment on why
	// that has to happen before this state is even readable).
	const expandCustomization = useCallback(() => setIsCustomizationExpanded(true), [])
	// The local half of the `−`: folds the section away and forgets both fields, mirroring the
	// single-object form's `collapseCustomization`.
	const collapseCustomization = useCallback(() => {
		setIsCustomizationExpanded(false)
		setCustomizationNote("")
		setCustomizationPriceAdjustment(null)
	}, [])

	return {
		fields,
		kind,
		sections,
		dimensionGroups,
		categorySelection,
		modelNumberTarget,
		sharedModelNumber,
		sharedDeliveryDate,
		sharedCustomModelNumber,
		/** True when the whole selection is already made of custom objects. */
		isCustomSelection: kind === "custom",
		imageUrl,
		previewMessage,
		showLnFt,
		mixedPlaceholder,
		resyncFromShared,
		/** `null` when the selected objects are not all at the same status. */
		status,
		selectedErefId: isMixed(fields.erefId) ? null : fields.erefId,
		width,
		setWidth,
		height,
		setHeight,
		depth,
		setDepth,
		adjustableShelves,
		setAdjustableShelves,
		notes,
		setNotes,
		name,
		setName,
		price,
		setPrice,
		hasLocks,
		setHasLocks,
		hasFinishedBacks,
		setHasFinishedBacks,
		fillings,
		setFillings,
		finishingEnds,
		setFinishingEnds,
		countertopGrommets,
		setCountertopGrommets,
		countertopWallCleats,
		setCountertopWallCleats,
		isCustomizationExpanded,
		isCustomizationNoteMixed,
		isCustomizationPriceAdjustmentMixed,
		customizationNote,
		setCustomizationNote,
		customizationPriceAdjustment,
		setCustomizationPriceAdjustment,
		expandCustomization,
		collapseCustomization,
	}
}
